import { ApolloError } from '@apollo/client';
import { Box, defaultTheme } from '@hover/blueprint';
import React, { useState } from 'react';
import { useParams } from 'react-router';
import { useSpring, useTransition } from 'react-spring';
import styled from 'styled-components';

import { Meta } from 'src/modules/designStudio/components/DesignStudio/Meta';
import { animationConfig } from 'src/modules/designStudio/helpers/animationConfig';
import { ErrorScope } from 'src/shared/constants';
import { useJobSharedThreeDFilesQuery } from 'src/shared/graphql/generated';
import { useErrorScope } from 'src/shared/hooks';
import { useQuery } from 'src/shared/hooks/useQuery';
import { useReactNativeWebView } from 'src/shared/hooks/useReactNativeWebView';

import { HistoryProvider } from '../../../../providers';
import { Background } from '../../../DesignStudio/Background';
import { Building } from '../../../DesignStudio/Building';
import { LoadingPage } from '../../../DesignStudio/LoadingPage';
import {
  DesignStudioContainer,
  MainContainer,
  PreventOverscroll,
} from '../../../DesignStudio/styled';
import { ShareErrorPage } from '../ShareErrorPage';

const Placeholder = styled(Box)`
  background-color: ${defaultTheme.colors.primaryBase};
`;

const renderPlaceholder = (error?: ApolloError) =>
  error ? (
    <ShareErrorPage />
  ) : (
    <Placeholder
      bottom={0}
      left={0}
      position="absolute"
      right={0}
      testId="placeholder"
      top={0}
    />
  );

const ShareView = () => {
  // Sets a tag on Sentry with the key of scope for easy filtering
  useErrorScope(ErrorScope.DESIGN_STUDIO);

  const { id } = useParams<{ id: string }>();
  const queryParams = useQuery();
  const shareToken = queryParams.get('share_token') ?? undefined;
  const jobId = parseInt(id, 10);

  const { data, error } = useJobSharedThreeDFilesQuery({
    variables: { jobId, shareToken: shareToken || '' },
  });

  const { geometryUrl, metadataUrl, authToken } =
    data?.jobSharedThreeDFiles ?? {};

  const [loading, setLoading] = useState(true);
  const [loadingBuilding, setLoadingBuilding] = useState(true);
  const loadingTransition = useTransition(
    loading,
    null,
    animationConfig.designProScreen,
  );

  const [isWebView] = useReactNativeWebView();

  const stillLoading = loading || loadingBuilding;

  const buildingTransition = useSpring({
    opacity: stillLoading ? 0 : 1,
    transform: stillLoading ? 'scale(1.2)' : 'scale(1)',
  });

  return authToken ? (
    <HistoryProvider
      authToken={authToken}
      jobId={jobId}
      shareToken={shareToken}
    >
      {/* If mobile app, restrict user scaling of document */}
      {isWebView && <Meta />}
      <DesignStudioContainer
        bottom={0}
        height="100%"
        position="fixed"
        top={0}
        width="100%"
      >
        <PreventOverscroll />
        <MainContainer style={buildingTransition}>
          <Background />
          {geometryUrl && metadataUrl && (
            <Building
              canvasHeight="100%"
              geometryUrl={geometryUrl}
              jobId={jobId}
              menu={null}
              metadataUrl={metadataUrl}
              setLoadingBuilding={setLoadingBuilding}
            />
          )}
        </MainContainer>
        {loadingTransition.map(({ item: isLoading, props: styles, key }) =>
          isLoading ? (
            <MainContainer key={key} style={styles}>
              <LoadingPage finishLoading={() => setLoading(false)} />
            </MainContainer>
          ) : (
            <Box display="block" key={key} />
          ),
        )}
      </DesignStudioContainer>
    </HistoryProvider>
  ) : (
    renderPlaceholder(error)
  );
};

export { ShareView };
